<div class="bg-background min-h-screen text-on-surface">

  <!-- Hero -->
  <section class="relative pt-24 pb-20 overflow-hidden">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute top-0 left-1/3 w-[700px] h-[500px] rounded-full bg-primary/5 blur-[140px]"></div>
    </div>
    <div class="max-w-7xl mx-auto px-6 relative z-10">
      <div class="max-w-3xl">
        <span class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-container text-on-secondary-container text-xs font-bold uppercase tracking-wider mb-6">
          <span class="material-symbols-outlined text-[14px]">info</span>
          Our Story
        </span>
        <h1 class="text-5xl md:text-6xl font-extrabold tracking-tighter text-on-background mb-6 leading-[1.1]">
          We're building the<br>
          <span class="bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary-container">operating system for enterprise.</span>
        </h1>
        <p class="text-xl text-on-surface-variant leading-relaxed max-w-2xl">
          ZAD ERP was founded on a simple belief: businesses in the Middle East deserve world-class enterprise software that's built for their reality — their regulations, their languages, their scale.
        </p>
      </div>
    </div>
  </section>

  <!-- Mission & Stats -->
  <section class="py-16 bg-surface-container-low border-y border-outline-variant/10">
    <div class="max-w-7xl mx-auto px-6">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        <div>
          <p class="text-4xl font-extrabold text-primary mb-2">1,000+</p>
          <p class="text-sm font-bold text-on-surface-variant">Active Businesses</p>
        </div>
        <div>
          <p class="text-4xl font-extrabold text-primary mb-2">4</p>
          <p class="text-sm font-bold text-on-surface-variant">Countries Served</p>
        </div>
        <div>
          <p class="text-4xl font-extrabold text-primary mb-2">6+</p>
          <p class="text-sm font-bold text-on-surface-variant">Platform Modules</p>
        </div>
        <div>
          <p class="text-4xl font-extrabold text-primary mb-2">2018</p>
          <p class="text-sm font-bold text-on-surface-variant">Year Founded</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Mission Statement -->
  <section class="py-24 bg-surface">
    <div class="max-w-7xl mx-auto px-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <div>
          <span class="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4 block">Our Mission</span>
          <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight text-on-background mb-6">To make enterprise intelligence accessible to every growing business.</h2>
          <p class="text-on-surface-variant leading-relaxed mb-6">
            For too long, powerful ERP systems were only available to the world's largest corporations. ZAD ERP changes that equation — bringing architectural-grade software to mid-market businesses with the speed, simplicity, and precision they deserve.
          </p>
          <p class="text-on-surface-variant leading-relaxed">
            We believe that when businesses operate with clarity, efficiency, and confidence, they grow faster and serve their communities better. That mission drives every line of code we write.
          </p>
        </div>
        <div class="grid grid-cols-2 gap-4">
          @for (value of values; track value.title) {
            <div class="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10">
              <span class="material-symbols-outlined text-primary text-2xl mb-3 block">{{value.icon}}</span>
              <h3 class="font-bold text-on-surface mb-2 text-sm">{{value.title}}</h3>
              <p class="text-xs text-on-surface-variant leading-relaxed">{{value.description}}</p>
            </div>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Timeline -->
  <section class="py-24 bg-surface-container-low">
    <div class="max-w-5xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight text-on-background mb-4">Our journey so far</h2>
        <p class="text-on-surface-variant max-w-xl mx-auto">From a startup with one module to a multi-country platform trusted by thousands.</p>
      </div>

      <div class="relative">
        <!-- Vertical line -->
        <div class="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-outline-variant/30 -translate-x-1/2"></div>

        <div class="space-y-12">
          @for (milestone of milestones; track milestone.year; let isEven = $even) {
            <div class="relative flex items-start gap-6 md:gap-0"
              [ngClass]="isEven ? 'md:flex-row' : 'md:flex-row-reverse'">
              <!-- Dot -->
              <div class="absolute left-6 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-surface-container-low z-10 mt-1 flex-shrink-0"></div>

              <!-- Content -->
              <div class="w-full md:w-1/2 pl-12 md:pl-0"
                [ngClass]="isEven ? 'md:pr-12' : 'md:pl-12'">
                <div class="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/10">
                  <span class="text-xs font-bold uppercase tracking-widest text-primary">{{milestone.year}}</span>
                  <h3 class="font-bold text-on-surface mt-1 mb-2">{{milestone.event}}</h3>
                  <p class="text-sm text-on-surface-variant leading-relaxed">{{milestone.detail}}</p>
                </div>
              </div>

              <!-- Spacer for alternating side -->
              <div class="hidden md:block w-1/2"></div>
            </div>
          }
        </div>
      </div>
    </div>
  </section>

  <!-- Team -->
  <section class="py-24 bg-surface">
    <div class="max-w-7xl mx-auto px-6">
      <div class="text-center mb-16">
        <h2 class="text-3xl md:text-4xl font-extrabold tracking-tight text-on-background mb-4">The people behind the platform</h2>
        <p class="text-on-surface-variant max-w-xl mx-auto">A team of seasoned engineers, product thinkers, and enterprise specialists united by a shared mission.</p>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        @for (member of team; track member.name) {
          <div class="bg-surface-container-lowest rounded-[2rem] p-8 border border-outline-variant/10 hover:border-primary/20 transition-all hover:-translate-y-1">
            <!-- Avatar -->
            <div class="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary-container flex items-center justify-center text-white font-extrabold text-xl mb-6">
              {{member.initials}}
            </div>
            <h3 class="font-bold text-on-surface mb-1">{{member.name}}</h3>
            <p class="text-xs font-bold text-primary uppercase tracking-wide mb-3">{{member.role}}</p>
            <p class="text-sm text-on-surface-variant leading-relaxed">{{member.bio}}</p>
          </div>
        }
      </div>
    </div>
  </section>

  <!-- Culture / Values Banner -->
  <section class="py-20 bg-surface-container-low">
    <div class="max-w-7xl mx-auto px-6">
      <div class="bg-gradient-to-r from-primary to-primary-container rounded-[2rem] p-10 md:p-16 text-white overflow-hidden relative">
        <div class="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-[60px]"></div>
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 relative z-10">
          <div class="max-w-xl">
            <h2 class="text-2xl md:text-3xl font-extrabold tracking-tight mb-4">Join us on the mission.</h2>
            <p class="text-white/80 leading-relaxed">We're always looking for passionate engineers, product designers, and enterprise specialists to help us build the future of business software.</p>
          </div>
          <div class="flex flex-col gap-3 flex-shrink-0">
            <a routerLink="/contact" class="bg-white text-primary px-8 py-4 rounded-xl font-bold text-center hover:shadow-lg transition-all">View Open Roles</a>
            <a routerLink="/contact" class="text-white/80 text-sm text-center hover:text-white transition-colors">Reach out to us →</a>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- CTA -->
  <section class="py-24 bg-on-background text-white overflow-hidden relative">
    <div class="max-w-7xl mx-auto px-6 text-center relative z-10">
      <h2 class="text-4xl md:text-5xl font-black tracking-tight mb-6">Ready to work with us?</h2>
      <p class="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">Start your free trial today or reach out to our team to discuss your specific needs.</p>
      <div class="flex flex-col sm:flex-row justify-center gap-4">
        <a routerLink="/create-account" class="bg-primary text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary-container transition-all">Get Started</a>
        <a routerLink="/contact" class="border border-outline-variant/30 text-white px-10 py-4 rounded-xl font-bold text-lg hover:bg-white/5 transition-all">Contact Us</a>
      </div>
    </div>
    <div class="absolute -bottom-24 -right-24 w-96 h-96 bg-primary/20 rounded-full blur-[120px]"></div>
    <div class="absolute -top-24 -left-24 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]"></div>
  </section>

</div>
